/**
 * The socket to the game server: joins a room with a match ticket (or resumes a held seat with
 * a reconnection token), registers every message handler before asking for the welcome (the
 * SDK warns about unhandled message types, and the console gate treats warnings as failures),
 * validates what arrives with the shared schemas, and routes both directions through the
 * latency conditioner so the dev tool can shape the link.
 */
import { Client, type Room } from '@colyseus/sdk';
import { PROTOCOL_VERSION } from '@steelwake/shared';
import {
  StatusMessageSchema,
  WelcomeMessageSchema,
  type StatusMessage,
  type WelcomeMessage,
} from '@steelwake/shared/net';

import { Conditioner, NO_CONDITIONS, type NetConditions } from './conditioner.js';

export const GAME_SERVER_URL = import.meta.env.VITE_PUBLIC_GAME_SERVER_URL ?? 'ws://localhost:2567';

/** The HTTP form of the game server address (its routes live on the same server). */
export function gameServerHttpUrl(): string {
  return GAME_SERVER_URL.replace(/^ws(s?):\/\//, 'http$1://');
}

export interface RoomHandlers {
  onSnapshot(message: unknown): void;
  onStatus(message: StatusMessage): void;
  /** The room is gone for good (a consented leave, the end of the match, or a failed resume). */
  onLeave(code: number): void;
  /** Our socket dropped; the SDK is trying to resume the seat. */
  onDrop(code: number): void;
  /** The SDK resumed the seat on the same room object. */
  onReconnect(): void;
}

export interface JoinOptions {
  roomId: string;
  ticket: string | null;
  /** A token from an earlier join of this room (a reload mid-match) tried first. */
  reconnectionToken: string | null;
  conditions: NetConditions | null;
}

export interface JoinedRoom {
  room: Room;
  welcome: WelcomeMessage;
  /** Resumes the seat after a reload while the server still holds it. */
  reconnectionToken: string;
  /** Round trip of the hello/welcome handshake: the clock's first measurement. */
  rttMs: number;
  /** Sends a room message through the conditioner. */
  send(type: string, payload: unknown): void;
  conditioner: Conditioner;
  /** Removes every handler registered by `joinMatchRoom` and stops the conditioner. */
  unsubscribe(): void;
}

const WELCOME_TIMEOUT_MS = 10_000;

/** Joins `roomId`, wires the handlers, and resolves once the server has welcomed us. */
export async function joinMatchRoom(
  opts: JoinOptions,
  handlers: RoomHandlers,
  signal: AbortSignal,
): Promise<JoinedRoom> {
  // A torn-down game (StrictMode's first mount, a quick navigation) must never take a seat.
  signal.throwIfAborted();
  const client = new Client(GAME_SERVER_URL);
  const conditioner = new Conditioner(opts.conditions ?? NO_CONDITIONS);
  let room: Room | null = null;
  if (opts.reconnectionToken) {
    // A held seat first; if the server let it go, fall back to the ticket.
    try {
      room = await client.reconnect(opts.reconnectionToken);
    } catch {
      room = null;
    }
  }
  if (!room) {
    if (!opts.ticket) throw new Error('No ticket for this match: join it from the queue.');
    room = await client.joinById(opts.roomId, { ticket: opts.ticket });
  }
  const joined = room;
  if (signal.aborted) {
    void joined.leave(true);
    signal.throwIfAborted();
  }
  const offSnap = joined.onMessage('snap', (payload: unknown) =>
    conditioner.receive(() => handlers.onSnapshot(payload)),
  );
  const offStatus = joined.onMessage('status', (payload: unknown) => {
    const parsed = StatusMessageSchema.safeParse(payload);
    if (parsed.success) conditioner.receive(() => handlers.onStatus(parsed.data));
  });
  const onLeave = (code: number) => handlers.onLeave(code);
  const onDrop = (code: number) => handlers.onDrop(code);
  const onReconnect = () => handlers.onReconnect();
  joined.onLeave(onLeave);
  joined.onDrop(onDrop);
  joined.onReconnect(onReconnect);
  const send = (type: string, payload: unknown) =>
    conditioner.send(() => {
      joined.send(type, payload);
    });
  const unsubscribe = () => {
    offSnap();
    offStatus();
    joined.onLeave.remove(onLeave);
    joined.onDrop.remove(onDrop);
    joined.onReconnect.remove(onReconnect);
    conditioner.dispose();
  };
  const helloAt = performance.now();
  const welcome = await new Promise<WelcomeMessage>((resolve, reject) => {
    const fail = (err: unknown) => {
      cleanup();
      unsubscribe();
      void joined.leave(true);
      reject(err instanceof Error ? err : new Error(String(err)));
    };
    const timer = setTimeout(
      () => fail(new Error('the match server did not answer')),
      WELCOME_TIMEOUT_MS,
    );
    const offWelcome = joined.onMessage('welcome', (payload: unknown) => {
      cleanup();
      const protocol = (payload as { protocol?: unknown } | null)?.protocol;
      if (typeof protocol === 'number' && protocol !== PROTOCOL_VERSION) {
        fail(new Error('the game was updated; reload the page to play'));
        return;
      }
      const parsed = WelcomeMessageSchema.safeParse(payload);
      if (parsed.success) resolve(parsed.data);
      else fail(new Error('the match server sent an unreadable welcome'));
    });
    const onEarlyLeave = (code: number) =>
      fail(new Error(`left the match before it began (code ${String(code)})`));
    joined.onLeave(onEarlyLeave);
    const onAbort = () => fail(new Error('cancelled'));
    signal.addEventListener('abort', onAbort, { once: true });
    function cleanup(): void {
      clearTimeout(timer);
      offWelcome();
      joined.onLeave.remove(onEarlyLeave);
      signal.removeEventListener('abort', onAbort);
    }
    send('hello', { t: 'hello' });
  });
  return {
    room: joined,
    welcome,
    reconnectionToken: joined.reconnectionToken,
    rttMs: performance.now() - helloAt,
    send,
    conditioner,
    unsubscribe,
  };
}
